/* ============================================================
   Mistiq — Find Your Scent

   A five-question quiz that scores the fragrance catalogue against
   the tags it already carries, and returns three matches with a
   plain-English reason for each.

   Design notes:

   - The questions and their tag weights live here, not in the section
     schema. Turning 5 questions x 4 answers x N tag weights into theme
     settings would produce an unusable settings panel. Copy that a
     merchant realistically wants to change (page heading, intro,
     results heading) IS in the schema.

   - Scoring is a plain weighted sum over tags the products already
     have. No metafields, no app, no new product data.

   - The product list is rendered into the page by the section, so
     results are instant and there is no network round trip.

   - Result copy is generated here from a controlled vocabulary. It
     never echoes product subtitles or any comparison field.

   - Mistiq sells eau de parfum only. There is no format question,
     because there is no second format to choose between.
   ============================================================ */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     Questions

     w      = tag weights applied when the answer is chosen
     pool   = hard filter on tags (gender). Products outside it are only
              used to top up a short result list.
     prefer = soft filter on tags (scent family). Within the pool,
              products carrying one of these are ranked ahead of every
              product that does not, whatever they score.

     The two filters give three tiers: in-pool and in-family first,
     then in-pool only, then everything else. Weights order the results
     inside each tier — so the other questions decide WHICH sweet
     fragrance you get, not whether you get a sweet one at all.

     Because the pool question is asked first, any later answer whose
     `prefer` tags do not exist inside that pool is never offered. There
     are no masculine florals, so someone who picked For him is not
     shown Floral and then quietly handed something else. See
     optionsFor().
     ---------------------------------------------------------- */

  var QUESTIONS = [
    {
      q: 'Who are you shopping for?',
      help: 'This narrows the shortlist before anything else, so the rest of your answers are judged against the right range.',
      a: [
        /* `pool` is a hard filter, not a weight. Someone shopping for a
           man who is handed three feminine florals has been ignored,
           however well those florals score on everything else.

           Each option maps to exactly one tag. A fragrance tagged both
           men and unisex therefore appears under For him and under
           Unisex, which is correct — it is genuinely both. */
        { t: 'For him', d: 'The masculine range', w: {}, pool: ['men'] },
        { t: 'For her', d: 'The feminine range', w: {}, pool: ['women'] },
        { t: 'Unisex', d: 'Built to wear well on anyone', w: {}, pool: ['unisex'] }
      ]
    },
    {
      q: 'How do you want to come across?',
      help: "There's no wrong answer — pick the one that sounds most like you on a good day.",
      a: [
        { t: 'Calm and easy', d: 'Quiet, unbothered, comfortable', w: { soft: 3, versatile: 2, everyday: 2 } },
        { t: 'Put together', d: 'Crisp and deliberate', w: { elegant: 3, office: 2, versatile: 1 } },
        { t: 'Noticeable', d: "You don't mind being remembered", w: { bold: 3, evening: 1 } }
      ]
    },
    {
      q: 'When will you actually wear it?',
      help: 'Think about the version of your week that happens most often.',
      a: [
        { t: 'Day to day', d: 'Work, errands, ordinary Tuesdays', w: { everyday: 3, office: 2, versatile: 1 } },
        { t: 'Evenings out', d: 'Dinner, drinks, later things', w: { evening: 3, bold: 1 } },
        { t: 'Date nights', d: 'When it matters who notices', w: { romantic: 3, evening: 2 } },
        { t: 'All of it', d: 'One bottle, no thinking', w: { versatile: 3, everyday: 2 } }
      ]
    },
    {
      q: 'Which of these sounds best to you?',
      help: 'Go on instinct. This is the question that does most of the work.',
      a: [
        /* `prefer` makes this the question that actually decides the
           result, as the help text promises. Weighting alone was not
           enough: in the thinner corners of the catalogue a fragrance
           could win on four weak signals while ignoring the family the
           customer picked. */
        { t: 'Clean and fresh', d: 'Citrus, sea air, cut grass', w: { fresh: 6, aquatic: 4 }, prefer: ['fresh', 'aquatic'] },
        { t: 'Warm and woody', d: 'Cedar, sandalwood, dry earth', w: { woody: 6, earthy: 3, aromatic: 3 }, prefer: ['woody', 'earthy', 'aromatic'] },
        { t: 'Sweet and rich', d: 'Vanilla, coffee, something edible', w: { gourmand: 6, sweet: 4 }, prefer: ['gourmand', 'sweet'] },
        { t: 'Floral', d: 'Rose, jasmine, orange blossom', w: { floral: 6 }, prefer: ['floral'] }
      ]
    },
    {
      q: 'And when the weather turns?',
      help: 'Fragrance behaves differently in heat and in cold.',
      a: [
        { t: 'I want it for summer', d: 'Light, bright, easy in heat', w: { summer: 3, fresh: 2, aquatic: 2 } },
        { t: 'I want it for the cold', d: 'Deeper, warmer, closer', w: { winter: 3, spicy: 2, oriental: 2, leather: 1 } },
        { t: 'All year round', d: 'It should just work', w: { versatile: 3, everyday: 2 } }
      ]
    }
  ];

  /* ----------------------------------------------------------
     Result copy

     A controlled vocabulary. Everything a customer reads on the
     results screen is composed from these fragments plus the product
     title and price — never from a comparison or subtitle field.
     ---------------------------------------------------------- */

  var FAMILY = {
    fresh: 'fresh', aquatic: 'aquatic', woody: 'woody', earthy: 'earthy',
    aromatic: 'aromatic', floral: 'floral', gourmand: 'gourmand', sweet: 'sweet',
    oriental: 'ambery', spicy: 'spicy', leather: 'leathery', smoky: 'smoky',
    chypre: 'mossy'
  };

  var OCCASION = {
    everyday: 'easy to wear day to day',
    office: 'quiet enough for work',
    evening: 'built for evenings',
    romantic: 'good for a night that matters',
    sporty: 'good for warm, active days'
  };

  var SEASON = {
    summer: 'at its best in warm weather',
    winter: 'at its best in the cold'
  };

  /* ----------------------------------------------------------
     Capped tags

     These describe how a fragrance fits into a life, not what it
     smells like. Three of the five questions can weight them, so when
     summed they out-scored the scent family question — a customer who
     asked for something sweet was being handed a woody scent purely
     because it happened to be tagged versatile and everyday as well.

     For these tags we take the strongest single signal rather than
     stacking. Answering "easy-going" three different ways is still one
     preference, not three.
     ---------------------------------------------------------- */

  var CAPPED = { versatile: true, everyday: true };

  var CHARACTER = {
    bold: 'it makes itself known',
    elegant: 'it stays composed',
    soft: 'it stays close to the skin',
    versatile: 'it works almost anywhere',
    playful: "it doesn't take itself too seriously"
  };

  /* Does this product carry any of these tags? A null tag list means
     "no filter", so everything qualifies. */
  function carries(product, tags) {
    if (!tags) return true;
    for (var i = 0; i < tags.length; i++) {
      if (product.g.indexOf(tags[i]) !== -1) return true;
    }
    return false;
  }

  function titleCase(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function listOf(map, tags, cap) {
    var out = [];
    for (var i = 0; i < tags.length && out.length < cap; i++) {
      if (map[tags[i]] && out.indexOf(map[tags[i]]) === -1) out.push(map[tags[i]]);
    }
    return out;
  }

  function reasonFor(product, chosen) {
    /* Only the tags the customer actually asked for, strongest first. */
    var matched = product.g
      .filter(function (t) { return chosen[t]; })
      .sort(function (a, b) { return chosen[b] - chosen[a]; });

    var families = listOf(FAMILY, matched, 2);

    /* If nothing in the family overlapped — which happens when the pool
       filter leaves a small set, so the best available match is simply
       not the family they asked for — describe what the fragrance
       actually is, from its own tags. Better an accurate description than
       a vague claim that it matches. */
    if (!families.length) families = listOf(FAMILY, product.g, 2);

    var occasions = listOf(OCCASION, matched, 1);
    var seasons = listOf(SEASON, matched, 1);
    var characters = listOf(CHARACTER, matched, 1);

    var parts = [];
    if (families.length) parts.push(families.join(' and '));
    if (occasions.length) parts.push(occasions[0]);
    if (seasons.length) parts.push(seasons[0]);

    var sentence = parts.join(', ');
    if (!sentence) return 'The closest fit for the answers you gave.';

    sentence = titleCase(sentence);
    if (characters.length) sentence += ' — and ' + characters[0];

    return sentence + '.';
  }

  /* ----------------------------------------------------------
     Component
     ---------------------------------------------------------- */

  function ScentQuiz(root) {
    this.root = root;
    this.step = 0;
    this.answers = [];

    var data = root.querySelector('[data-quiz-products]');
    try {
      this.products = JSON.parse(data.textContent) || [];
    } catch (e) {
      this.products = [];
    }

    this.shell = root.querySelector('[data-quiz-shell]');
    this.fallback = root.querySelector('[data-quiz-fallback]');
    this.stage = root.querySelector('[data-quiz-stage]');
    this.results = root.querySelector('[data-quiz-results]');
    this.progress = root.querySelector('[data-quiz-progress]');
    this.bar = root.querySelector('[data-quiz-bar]');
    this.status = root.querySelector('[data-quiz-status]');

    /* Nothing to score against — leave the no-JS fallback in place
       rather than showing an empty quiz. */
    if (!this.products.length) return;

    this.fallback.hidden = true;
    this.shell.hidden = false;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Bound once, on the container that survives every re-render. Binding
       inside render() would stack a new listener per question and fire
       choose() repeatedly. */
    var self = this;
    this.stage.addEventListener('change', function (e) {
      if (!e.target.classList || !e.target.classList.contains('mq-quiz__radio')) return;
      self.choose(parseInt(e.target.value, 10));
    });

    this.render();
  }

  ScentQuiz.prototype.total = function () {
    return QUESTIONS.length;
  };

  /* The pool chosen at an earlier step, if any. */
  ScentQuiz.prototype.poolSoFar = function (step) {
    for (var s = 0; s < step; s++) {
      if (this.answers[s] && this.answers[s].pool) return this.answers[s].pool;
    }
    return null;
  };

  /* The answers worth showing for a step, given what has been chosen
     already. An answer that would filter the pool down to nothing is
     dropped rather than offered and then silently overridden.

     If that leaves nothing at all — only possible if the catalogue
     loses an entire pool — fall back to the full list so the quiz can
     never dead-end on a blank question. */
  ScentQuiz.prototype.optionsFor = function (step) {
    var all = QUESTIONS[step].a;
    var pool = this.poolSoFar(step);
    if (!pool) return all;

    var products = this.products;

    var usable = all.filter(function (answer) {
      if (!answer.prefer) return true;
      for (var i = 0; i < products.length; i++) {
        if (carries(products[i], pool) && carries(products[i], answer.prefer)) return true;
      }
      return false;
    });

    return usable.length ? usable : all;
  };

  ScentQuiz.prototype.render = function () {
    var self = this;
    var q = QUESTIONS[this.step];

    this.progress.hidden = false;
    this.results.hidden = true;
    this.stage.hidden = false;

    this.root.querySelector('[data-quiz-current]').textContent = this.step + 1;
    this.root.querySelector('[data-quiz-total]').textContent = this.total();
    this.bar.style.setProperty('--quiz-progress', (this.step / this.total()) * 100 + '%');

    var html = '';
    html += '<fieldset class="mq-quiz__fieldset">';
    html += '<legend class="mq-quiz__legend">';
    html += '<span class="mq-quiz__question" tabindex="-1" data-quiz-heading>' + esc(q.q) + '</span>';
    html += '<span class="mq-quiz__help">' + esc(q.help) + '</span>';
    html += '</legend>';
    html += '<div class="mq-quiz__options">';

    /* Held on the instance because choose() indexes into it, and it is
       narrower than QUESTIONS[step].a whenever an earlier answer has
       ruled options out. */
    this.visible = this.optionsFor(this.step);

    this.visible.forEach(function (answer, i) {
      var id = 'mq-q' + self.step + '-a' + i;
      html += '<label class="mq-quiz__option" for="' + id + '">';
      html += '<input class="mq-quiz__radio" type="radio" name="mq-q' + self.step + '" id="' + id + '" value="' + i + '">';
      html += '<span class="mq-quiz__option-body">';
      html += '<span class="mq-quiz__option-title">' + esc(answer.t) + '</span>';
      html += '<span class="mq-quiz__option-desc">' + esc(answer.d) + '</span>';
      html += '</span>';
      html += '</label>';
    });

    html += '</div></fieldset>';

    if (this.step > 0) {
      html += '<button type="button" class="mq-quiz__back" data-quiz-back>Back</button>';
    }

    this.stage.innerHTML = html;

    var back = this.stage.querySelector('[data-quiz-back]');
    if (back) back.addEventListener('click', function () { self.goBack(); });

    /* Announce the new question, and move focus to it so a keyboard or
       screen reader user is not left behind on the previous step. */
    this.status.textContent = 'Question ' + (this.step + 1) + ' of ' + this.total() + '. ' + q.q;
    var heading = this.stage.querySelector('[data-quiz-heading]');
    if (heading && this.step > 0) heading.focus();
  };

  ScentQuiz.prototype.choose = function (index) {
    var self = this;

    /* Store the answer itself, not its index. The visible list varies
       by step depending on earlier answers, so an index is only
       meaningful alongside the list it came from. */
    this.answers[this.step] = this.visible[index];

    /* Changing an earlier answer can invalidate later ones — picking
       For him after having picked Floral, say. Drop everything after
       this step; the customer re-answers on the way forward. */
    this.answers.length = this.step + 1;

    /* Let the selected state register before moving on. Without the
       pause the card appears to do nothing and the page just changes. */
    var wait = this.reduced ? 0 : 260;

    setTimeout(function () {
      if (self.step < self.total() - 1) {
        self.step++;
        self.render();
      } else {
        self.finish();
      }
    }, wait);
  };

  ScentQuiz.prototype.goBack = function () {
    if (this.step === 0) return;
    this.step--;
    this.render();
  };

  ScentQuiz.prototype.score = function () {
    var chosen = {};
    var pool = null;
    var prefer = null;

    for (var s = 0; s < this.answers.length; s++) {
      var answer = this.answers[s];
      if (!answer) continue;

      for (var tag in answer.w) {
        var add = answer.w[tag];
        chosen[tag] = CAPPED[tag]
          ? Math.max(chosen[tag] || 0, add)
          : (chosen[tag] || 0) + add;
      }

      if (answer.pool) pool = answer.pool;
      if (answer.prefer) prefer = answer.prefer;
    }

    function rank(list) {
      return list
        .map(function (p) {
          var total = 0;
          var hits = 0;

          for (var i = 0; i < p.g.length; i++) {
            if (chosen[p.g[i]]) {
              total += chosen[p.g[i]];
              hits++;
            }
          }

          return { p: p, score: total, hits: hits };
        })
        .sort(function (a, b) {
          if (b.score !== a.score) return b.score - a.score;
          /* Same score: prefer the one that matched more of what they
             asked for, rather than one tag weighted heavily. */
          if (b.hits !== a.hits) return b.hits - a.hits;
          return a.p.t.localeCompare(b.p.t);
        });
    }

    /* Three tiers, best first:
         0  right gender, right family
         1  right gender, wrong family
         2  wrong gender

       Tiers are concatenated rather than trimmed, so a thin corner of
       the catalogue still fills three result slots instead of showing a
       short list. When that happens the result copy describes what the
       fragrance actually is rather than claiming it matches — see
       reasonFor(). */
    var tiers = [[], [], []];

    for (var i = 0; i < this.products.length; i++) {
      var p = this.products[i];
      var tier = carries(p, pool) ? (carries(p, prefer) ? 0 : 1) : 2;
      tiers[tier].push(p);
    }

    return {
      ranked: rank(tiers[0]).concat(rank(tiers[1]), rank(tiers[2])),
      chosen: chosen
    };
  };

  ScentQuiz.prototype.finish = function () {
    var self = this;
    var result = this.score();
    var top = result.ranked.slice(0, 3);

    var html = '';

    top.forEach(function (entry, i) {
      var p = entry.p;
      html += '<li class="mq-quiz__match">';
      html += '<a class="mq-quiz__match-link" href="' + esc(p.u) + '">';
      html += '<span class="mq-quiz__match-media">';
      if (p.i) {
        html += '<img src="' + esc(p.i) + '" alt="" width="500" height="625" loading="lazy" decoding="async">';
      }
      html += '</span>';
      html += '<span class="mq-quiz__match-body">';
      html += '<span class="mq-quiz__match-rank">' + (i === 0 ? 'Your closest match' : 'Also worth smelling') + '</span>';
      html += '<span class="mq-quiz__match-title">' + esc(p.t) + '</span>';
      html += '<span class="mq-quiz__match-reason">' + esc(reasonFor(p, result.chosen)) + '</span>';
      html += '<span class="mq-quiz__match-price">From ' + esc(p.p) + '</span>';
      html += '</span></a></li>';
    });

    this.root.querySelector('[data-quiz-matches]').innerHTML = html;

    this.stage.hidden = true;
    this.progress.hidden = true;
    this.results.hidden = false;
    this.bar.style.setProperty('--quiz-progress', '100%');

    this.status.textContent = 'Done. Here are your three closest matches.';

    var heading = this.results.querySelector('[data-quiz-results-heading]');
    if (heading) heading.focus();

    var restart = this.results.querySelector('[data-quiz-restart]');
    if (restart && !restart.mqBound) {
      restart.mqBound = true;
      restart.addEventListener('click', function () {
        self.step = 0;
        self.answers = [];
        self.render();
      });
    }
  };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-scent-quiz]:not([data-quiz-ready])').forEach(function (el) {
      el.setAttribute('data-quiz-ready', '');
      new ScentQuiz(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders the section on every settings change. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
